import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, FileText, Edit, Printer, Trash2, Upload } from 'lucide-react';
import api, { formatCurrency, settingsAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { formatDateDisplay } from '../lib/dateUtils';

export default function CollectionMemoView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [memo, setMemo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [advanceEngineEnabled, setAdvanceEngineEnabled] = useState(true);

  useEffect(() => {
    api.get(`/collection-memos/${id}`).then(r => setMemo(r.data))
      .catch(() => { toast.error('Not found'); navigate('/collection-memos'); })
      .finally(() => setLoading(false));
    settingsAPI.getAdvanceEngine().then(r => setAdvanceEngineEnabled(r.data?.enabled !== false)).catch(() => {});
  }, [id, navigate]);

  const downloadPDF = async () => {
    try {
      const res = await api.get(`/collection-memos/${id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = `${memo.memo_number}.pdf`; a.click();
      toast.success('PDF downloaded');
    } catch { toast.error('Download failed'); }
  };

  const printPDF = async () => {
    try {
      const res = await api.get(`/collection-memos/${id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const win = window.open(url, '_blank');
      if (win) { win.onload = () => { win.focus(); win.print(); }; }
    } catch { toast.error('Print failed'); }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await api.post('/company-profile/logo', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Logo updated! New PDFs will use this logo.');
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this collection memo?')) return;
    try { await api.delete(`/collection-memos/${id}`); toast.success('Deleted'); navigate('/collection-memos'); }
    catch { toast.error('Delete failed'); }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="w-6 h-6 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" /></div>;
  if (!memo) return null;

  return (
    <div className="max-w-4xl mx-auto space-y-5" data-testid="cm-view">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/collection-memos')} className="p-2 hover:bg-slate-100 rounded-md"><ArrowLeft className="w-5 h-5 text-slate-600" /></button>
          <div>
            <h1 className="font-heading font-bold text-2xl text-slate-900">{memo.memo_number}</h1>
            <p className="text-sm text-slate-500">Collection Memo &middot; {formatDateDisplay(memo.date)} &middot; {memo.template === 'template2' ? 'A&A Style' : 'MSP Style'}</p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <label className="btn-secondary flex items-center gap-2 cursor-pointer text-xs" data-testid="upload-logo-btn">
            <Upload className="w-4 h-4" /> {uploading ? 'Uploading...' : 'Upload Logo'}
            <input type="file" accept=".jpg,.jpeg,.png,.svg,.webp" onChange={handleLogoUpload} className="hidden" disabled={uploading} />
          </label>
          <button onClick={() => navigate(`/collection-memos/${id}/edit`)} className="btn-secondary flex items-center gap-2"><Edit className="w-4 h-4" /> Edit</button>
          <button onClick={printPDF} className="btn-secondary flex items-center gap-2" data-testid="print-btn"><Printer className="w-4 h-4" /> Print</button>
          <button onClick={downloadPDF} className="btn-primary flex items-center gap-2" data-testid="download-pdf-btn"><FileText className="w-4 h-4" /> Download PDF</button>
          <button onClick={handleDelete} className="text-red-500 hover:bg-red-50 p-2 rounded-md" data-testid="delete-btn"><Trash2 className="w-4 h-4" /></button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden shadow-sm">
        <div className="bg-slate-100 border-b border-slate-200 text-slate-900 p-5 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src="/images/transsolution_logo.png" alt="Logo" className="w-12 h-12 rounded-md object-contain bg-white p-0.5 border border-slate-200" />
            <div><h2 className="font-heading font-bold text-lg">{(user?.company?.name || 'Company').toUpperCase()}</h2><p className="text-slate-500 text-xs">Collection Memo</p></div>
          </div>
          <div className="text-right"><p className="text-slate-700 font-bold tracking-wider">COLLECTION MEMO</p><p className="text-slate-600 font-mono mt-1">{memo.memo_number}</p></div>
        </div>
        <div className="p-5 space-y-5">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {[['Date', memo.date],['Collection No', memo.collection_no],['Party', memo.party_name],['Attention', memo.attention],['Truck No.', memo.truck_no],['From', memo.from_location],['To', memo.to_location],['Weight', memo.weight]].map(([l,v],i) => v ? <div key={i}><p className="text-xs text-slate-500 uppercase">{l}</p><p className="font-mono text-sm font-medium">{v}</p></div> : null)}
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="border border-slate-200 rounded-md p-4 text-center"><p className="text-xs text-slate-500">Freight</p><p className="font-mono font-bold text-lg">Rs. {formatCurrency(memo.freight)}</p></div>
            {advanceEngineEnabled && (memo.advance || 0) > 0 && <div className="border border-slate-200 rounded-md p-4 text-center" data-testid="cm-advance"><p className="text-xs text-slate-500">Advance</p><p className="font-mono font-bold text-lg text-red-600">Rs. {formatCurrency(memo.advance)}</p></div>}
            <div className="border border-slate-200 rounded-md p-4 text-center"><p className="text-xs text-slate-500">Balance</p><p className="font-mono font-bold text-lg text-green-600">Rs. {formatCurrency(memo.balance)}</p></div>
          </div>
          <div className="flex justify-end">
            <div className="w-64 space-y-1">
              <div className="flex justify-between text-sm"><span className="text-slate-500">Round Off</span><span className="font-mono">{memo.round_off >= 0 ? '+' : ''}{memo.round_off?.toFixed(2)}</span></div>
              <div className="border-t-2 border-slate-900 pt-2 flex justify-between"><span className="font-heading font-bold">Total Payable</span><span className="font-mono font-bold text-xl" data-testid="total-payable">Rs. {formatCurrency(memo.total_payable)}</span></div>
            </div>
          </div>
          {(memo.guarantee || memo.payable_at || memo.extra_height || memo.detention_per_day || memo.loading_capacity) && (
            <div className="grid grid-cols-3 gap-3">
              {[['Guarantee', memo.guarantee],['Payable At', memo.payable_at],['Extra Height', memo.extra_height],['Detention/Day', memo.detention_per_day],['Loading Capacity', memo.loading_capacity]].map(([l,v],i) => v ? <div key={i}><p className="text-xs text-slate-500">{l}</p><p className="text-sm">{v}</p></div> : null)}
            </div>
          )}
          {(memo.terms || memo.notes) && <div className="border-t pt-3"><p className="text-xs text-slate-500 uppercase mb-1">Terms & Notes</p><pre className="text-xs text-slate-600 whitespace-pre-wrap">{memo.terms}{memo.notes ? `\n${memo.notes}` : ''}</pre></div>}
          <div className="border-t pt-3 text-center"><p className="text-[10px] text-slate-400">Generated by Transsolution ERP</p></div>
        </div>
      </div>
    </div>
  );
}
